/**
 * Shipping Date Utilities
 *
 * Calculates "ship by" dates from a business-day range and formats them
 * for display across PDP, cart, and checkout.
 *
 * Business days = weekdays only (Mon–Fri). No holiday calendar is applied.
 */

/**
 * Fallback business-day estimate used when scs_ship_by_date_format2 is on but
 * no real shipping range could be found for a SKU (e.g. it's missing from
 * window.scs_off_site_skus_full). We'd rather show an estimate than nothing.
 */
export const DEFAULT_SHIP_BY_DAYS = 2;

/**
 * Add N business days to a Date, skipping weekends and any dates in `holidays`.
 * Returns a new Date without mutating the input.
 *
 * Mirrors TagShipTime::addBusinessDays in scsbiz3.wearevisum.com (App\Actions),
 * which is the source of truth for how the backend computes promised-date tags.
 *
 * @param holidays  'YYYY-MM-DD' strings to treat as non-working days (e.g. from
 *                   GET /api/v1/public/holidays via HolidaysService).
 */
export function addBusinessDays(
  from: Date,
  days: number,
  holidays: string[] | Set<string> = [],
): Date {
  const holidaySet = holidays instanceof Set ? holidays : new Set(holidays);
  const result = new Date(from);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    const dow = result.getDay();
    if (dow !== 0 && dow !== 6 && !holidaySet.has(formatShipByDateISO(result))) {
      added += 1;
    }
  }
  return result;
}

/**
 * Ordinal suffix for a day number (1 → "st", 2 → "nd", 3 → "rd", etc.)
 */
function ordinal(n: number): string {
  if (n >= 11 && n <= 13) return "th";
  switch (n % 10) {
    case 1: return "st";
    case 2: return "nd";
    case 3: return "rd";
    default: return "th";
  }
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Format a Date as "Month Dayth", e.g. "August 3rd".
 */
export function formatShipByDate(date: Date): string {
  const month = MONTH_NAMES[date.getMonth()];
  const day = date.getDate();
  return `${month} ${day}${ordinal(day)}`;
}

/**
 * Format a Date as an ISO date string (YYYY-MM-DD), e.g. "2026-08-03".
 */
export function formatShipByDateISO(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Look up the shipping range ("7-9") for a given SKU from the
 * window.scs_off_site_skus_full array set by the admin/pricing tool.
 * Returns null if the SKU isn't found or the data isn't loaded.
 */
export function getShippingRangeBySku(sku: string | undefined | null): string | null {
  if (!sku) return null;
  if (
    typeof window === "undefined" ||
    !Array.isArray((window as any).scs_off_site_skus_full)
  ) {
    return null;
  }
  const entry = (window as any).scs_off_site_skus_full.find(
    (e: any) => e.sku === sku,
  );
  return entry?.shippingTime ?? null;
}

/**
 * Parse a shipping range string like "16-18" and return the max value.
 * Returns null if the input is empty/invalid.
 */
export function parseShippingRange(range: string | undefined | null): number | null {
  if (!range) return null;
  const parts = range.split("-").map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
  if (parts.length === 0) return null;
  // Take the maximum end of the range
  return Math.max(...parts);
}

/**
 * Calculate the "ships by" date from a shipping range string.
 *
 * @param range  e.g. "16-18" (min-max business days)
 * @param from   base date (default: today)
 * @returns      { date, formatted, iso } or null if range is invalid
 */
export function calculateShipByDate(
  range: string | undefined | null,
  from: Date = new Date(),
  holidays: string[] | Set<string> = [],
): { date: Date; formatted: string; iso: string } | null {
  const maxDays = parseShippingRange(range);
  if (maxDays === null) return null;

  const shipDate = addBusinessDays(from, maxDays, holidays);
  return {
    date: shipDate,
    formatted: formatShipByDate(shipDate),
    iso: formatShipByDateISO(shipDate),
  };
}

/**
 * Given a list of SKUs (e.g. the whole cart's contents), returns the
 * calculateShipByDate result for whichever SKU has the furthest-out date —
 * the cart's worst-case shipping time. SKUs with no entry in
 * window.scs_off_site_skus_full (or no shipping range) are skipped.
 * Returns null if none of the SKUs have a shipping range.
 */
export function getMaxShipByDate(
  skus: (string | undefined | null)[],
  from: Date = new Date(),
  holidays: string[] | Set<string> = [],
): { date: Date; formatted: string; iso: string } | null {
  let max: { date: Date; formatted: string; iso: string } | null = null;
  for (const sku of skus) {
    const range = getShippingRangeBySku(sku);
    const result = calculateShipByDate(range, from, holidays);
    if (result && (!max || result.date.getTime() > max.date.getTime())) {
      max = result;
    }
  }
  return max;
}
